// Details > Audience, as two groups side by side (prototype, see audienceGroups.ts):
// Read & Assess (required) and Read Only (optional). Each holds audience groups,
// added one at a time in a modal (roles, the org's other lists, a name).
// Details won't go on while someone could be in both; a note says who.
// With the backend on (audienceRequirementsBackend()), the groups are the
// program's audience as they are; a pair the backend still refuses (409, who
// would be in both) is highlighted, with its message in the note.

import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoTip from '../../../../../components/ui/InfoTip';
import { useOrgAudienceOptions } from '../../Program/components/learning-lifecycle/useOrgMemberAttributes';
import type { SetupRole } from '../capabilitySetup';
import { isRoleUsable } from '../sharedSets';
import {
  ActionButton,
  BODY,
  BRANDY,
  BRANDY_FILL,
  Chip,
  FONT,
  FormRow,
  INPUT_SX,
  LABEL,
  LINE,
  LINE_2,
  Note,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3
} from '../designSystem';
import { LineAction, type DetailsFieldProps } from './DetailsStep';
import { audienceRequirementsBackend } from '../../Program/components/program tabs/ProgramSettings/requirements/flags';
import {
  allRoleIds,
  audienceDescription,
  audienceName,
  describe,
  emptyGroups,
  isBlank,
  newAudience,
  overlaps,
  type AudienceDraft,
  type AudienceGroupsDraft
} from './audienceGroups';

type GroupKind = keyof AudienceGroupsDraft;
interface List {
  _id: string;
  name: string;
  values: Array<{ _id: string; value: string }>;
}

const toggled = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

const Required: React.FC = () => (
  <Box component="span" aria-label="required" sx={{ color: BRANDY }}>
    *
  </Box>
);

/** One list in the modal, full width: its name, then its options three to a row. */
const PickBlock: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({
  label,
  required = false,
  children
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ ...LABEL, color: BRANDY, mb: '8px' }}>
      {label}
      {required && <Required />}
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>{children}</Box>
  </Box>
);

/** Adding or editing one audience group: its roles, the org's other lists, and a name. */
const AudienceGroupModal: React.FC<{
  kind: GroupKind;
  initial: AudienceDraft;
  editing: boolean;
  roles: SetupRole[];
  usable: (r: SetupRole) => boolean;
  teamName: (id: string) => string;
  lists: List[];
  describeIt: (a: AudienceDraft) => string;
  onCancel: () => void;
  onConfirm: (a: AudienceDraft) => void;
}> = ({ kind, initial, editing, roles, usable, teamName, lists, describeIt, onCancel, onConfirm }) => {
  const [draft, setDraft] = useState<AudienceDraft>(initial);
  const teamIds = Array.from(new Set(roles.map((r) => r.teamId)));
  const needsRole = kind === 'readAssess';
  const ready = needsRole ? draft.roleIds.length > 0 : !isBlank(draft);
  const reason = needsRole ? 'Pick at least one role.' : 'Pick at least one role or attribute.';
  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { background: SURFACE, border: `1px solid ${LINE}`, borderRadius: '12px' } }}
    >
      <Box sx={{ px: '22px', pt: '18px', pb: '14px', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: TEXT }}>
            {editing ? 'Edit' : 'Add'} {needsRole ? 'Read & Assess' : 'Read Only'} Audience Group
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onCancel} sx={{ color: TEXT_3, p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {/* Every list at once, one beneath the next with a hairline between: nothing to scroll. */}
      <Box
        sx={{
          px: '22px',
          pb: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          '& > *:not(:first-of-type)': { borderTop: `1px solid ${LINE}`, pt: '16px' }
        }}
      >
        {/* Roles: one section, each team a subtitle with its roles three to a row. */}
        {teamIds.length > 0 && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...LABEL, color: BRANDY, mb: '10px' }}>
              Roles
              {needsRole && <Required />}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {teamIds.map((teamId) => {
                // The tick beside a team picks every role in it that can be picked (or clears them).
                const pickable = roles.filter((r) => r.teamId === teamId && usable(r)).map((r) => r.id);
                const on = pickable.filter((id) => draft.roleIds.includes(id)).length;
                const all = pickable.length > 0 && on === pickable.length;
                return (
                  <Box key={teamId}>
                    <Box
                      component="label"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        mb: '6px',
                        cursor: pickable.length ? 'pointer' : 'default'
                      }}
                    >
                      <Checkbox
                        size="small"
                        disableRipple
                        checked={all}
                        indeterminate={on > 0 && !all}
                        disabled={pickable.length === 0}
                        inputProps={{ 'aria-label': `All ${teamName(teamId)} roles` }}
                        onChange={() =>
                          setDraft({
                            ...draft,
                            roleIds: all
                              ? draft.roleIds.filter((id) => !pickable.includes(id))
                              : Array.from(new Set([...draft.roleIds, ...pickable]))
                          })
                        }
                        sx={{
                          p: 0,
                          color: TEXT_3,
                          '& .MuiSvgIcon-root': { fontSize: 16 },
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: BRANDY }
                        }}
                      />
                      <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3 }}>{teamName(teamId)}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                      {roles
                        .filter((r) => r.teamId === teamId)
                        .map((r) => (
                          <Chip
                            key={r.id}
                            multi
                            label={r.name}
                            selected={draft.roleIds.includes(r.id)}
                            disabled={!usable(r)}
                            reason="Confirm its capabilities and levels in Capabilities first."
                            onClick={() => setDraft({ ...draft, roleIds: toggled(draft.roleIds, r.id) })}
                          />
                        ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        {lists.map((list) => (
          <PickBlock key={list._id} label={list.name}>
            {list.values.map((v) => (
              <Chip
                key={v._id}
                multi
                label={v.value}
                selected={(draft.picks[list._id] ?? []).includes(v._id)}
                onClick={() =>
                  setDraft({
                    ...draft,
                    picks: { ...draft.picks, [list._id]: toggled(draft.picks[list._id] ?? [], v._id) }
                  })
                }
              />
            ))}
          </PickBlock>
        ))}
      </Box>
      <Box sx={{ px: '22px', pt: '14px', pb: '18px', borderTop: `1px solid ${LINE}` }}>
        <Typography sx={{ ...LABEL, color: TEXT_2, mb: '6px' }}>Name</Typography>
        <Box
          component="input"
          value={draft.name ?? ''}
          placeholder={isBlank(draft) ? 'Name this Audience Group' : describeIt(draft)}
          maxLength={60}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, name: e.target.value })}
          sx={INPUT_SX}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: '16px' }}>
          <ActionButton onClick={onCancel}>Cancel</ActionButton>
          <ActionButton primary disabled={!ready} reason={reason} onClick={() => onConfirm(draft)}>
            {editing ? 'Save' : 'Confirm'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

/** A group's title, styled like Owner and Publish by, a required mark when it is, and what it means behind an info mark. */
const GroupHead: React.FC<{ title: string; tip: string; required?: boolean }> = ({ title, tip, required = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
    <Typography sx={{ ...LABEL, color: TEXT_2 }}>
      {title}
      {required && <Required />}
    </Typography>
    <InfoTip title={tip} placement="bottom-start" />
  </Box>
);

export const AudienceGroupsField: React.FC<DetailsFieldProps> = ({
  details,
  set,
  roles,
  teamsListId,
  sets = [],
  groupsRefusal = null
}) => {
  const usable = (r: SetupRole) => isRoleUsable(r, sets);
  const { catalog, labelById } = useOrgAudienceOptions();
  const teamList =
    catalog.find((list) => list._id === teamsListId) ??
    catalog.find((list) => /^(teams?|departments?)$/i.test(list.name.trim())) ??
    null;
  const otherLists = catalog.filter((list) => list._id !== teamList?._id);
  const teamName = (id: string) => teamList?.values.find((v) => v._id === id)?.value ?? 'Other';
  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? 'Role';
  const valueName = (id: string) => labelById.get(id) ?? '';
  const listOrder = catalog.map((l) => l._id);
  const nameOf = (a: AudienceDraft) => audienceName(a, roleName, valueName, listOrder);
  const describeIt = (a: AudienceDraft) => audienceDescription(a, roleName, valueName, listOrder);

  // Groups saved before they had to be added one by one may hold an empty one: it doesn't count.
  // Opened with an audience chosen already (from a shared set, a recommendation, an older draft):
  // it becomes the first Read & Assess audience group, its roles and any values picked list by list.
  useEffect(() => {
    if (details.groups || details.roleIds.length === 0) return;
    const picks: Record<string, string[]> = {};
    for (const id of details.audienceIds) {
      const list = otherLists.find((l) => l.values.some((v) => v._id === id));
      if (list) picks[list._id] = [...(picks[list._id] ?? []), id];
    }
    set('groups', { readAssess: [{ ...newAudience(), roleIds: details.roleIds, picks }], readOnly: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const groups = details.groups ?? emptyGroups();
  const [modal, setModal] = useState<{ kind: GroupKind; audience: AudienceDraft; editing: boolean } | null>(null);
  const setGroups = (next: AudienceGroupsDraft) => {
    set('groups', next);
    // The backend takes the groups as the audience. DUMMY until it does: the program's own audience is
    // every role the groups name, so it is created and saved as before.
    if (audienceRequirementsBackend()) return;
    set('roleIds', allRoleIds(next));
    set('audienceIds', []);
  };
  const confirm = (kind: GroupKind, a: AudienceDraft) => {
    const exists = groups[kind].some((x) => x.id === a.id);
    setGroups({ ...groups, [kind]: exists ? groups[kind].map((x) => (x.id === a.id ? a : x)) : [...groups[kind], a] });
    setModal(null);
  };
  const remove = (kind: GroupKind, id: string) =>
    setGroups({ ...groups, [kind]: groups[kind].filter((x) => x.id !== id) });

  const found = overlaps(groups);
  // The pairs the backend found someone could be in both of (its 409), until the groups change.
  const refused = new Set(
    (groupsRefusal?.overlaps ?? []).flatMap((o) => [`readAssess:${o.readAssessId}`, `readOnly:${o.readOnlyId}`])
  );
  const serverNote = groupsRefusal?.overlaps?.length && found.length === 0 ? groupsRefusal.message : null;

  const column = (kind: GroupKind) => (
    <Box sx={{ minWidth: 0 }}>
      {groups[kind]
        .filter((a) => !isBlank(a))
        .map((a) => (
          <Box
            key={a.id}
            data-overlap={refused.has(`${kind}:${a.id}`) ? 'server' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: '10px',
              borderTop: `1px solid ${LINE}`,
              ...(refused.has(`${kind}:${a.id}`)
                ? { bgcolor: BRANDY_FILL, boxShadow: `inset 2px 0 0 ${BRANDY}`, px: '8px', mx: '-8px' }
                : {})
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ ...BODY, fontWeight: 600, color: TEXT }}>
                {nameOf(a)}
              </Typography>
              {a.name?.trim() && (
                <Typography noWrap sx={{ ...BODY, fontSize: 12, color: TEXT_3 }}>
                  {describeIt(a)}
                </Typography>
              )}
            </Box>
            <LineAction onClick={() => setModal({ kind, audience: a, editing: true })}>Edit</LineAction>
            <LineAction onClick={() => remove(kind, a.id)}>Remove</LineAction>
          </Box>
        ))}
      <Box
        component="button"
        type="button"
        onClick={() => setModal({ kind, audience: newAudience(), editing: false })}
        sx={{
          mt: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          height: 28,
          px: '10px',
          border: `1px solid ${LINE_2}`,
          borderRadius: '4px',
          bgcolor: 'transparent',
          color: TEXT,
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          '&:hover': { borderColor: BRANDY, color: BRANDY }
        }}
      >
        + Add Audience Group
      </Box>
    </Box>
  );

  return (
    <FormRow required label="Audience">
      <Box>
        {roles.length === 0 && (
          <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3, mb: '10px' }}>
            No roles yet. Add them in Capabilities.
          </Typography>
        )}
        {/* The two groups side by side, a hairline between them. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
          <Box sx={{ pr: '20px' }}>
            <GroupHead
              title="Read & Assess"
              required
              tip="Read and are assessed on the lessons for their own role’s capabilities, and read the rest."
            />
            {column('readAssess')}
          </Box>
          <Box sx={{ pl: '20px', borderLeft: `1px solid ${LINE}` }}>
            <GroupHead title="Read Only" tip="Read every lesson to stay in the loop. Never assessed." />
            {column('readOnly')}
          </Box>
        </Box>
        {found.length > 0 && (
          <Box sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {found.map((o) => (
              <Note key={`${o.readAssess.id}-${o.readOnly.id}`}>
                {describe(o.who, roleName, valueName, listOrder)} would be in both {nameOf(o.readAssess)} and{' '}
                {nameOf(o.readOnly)}. Narrow one so they’re in only one.
              </Note>
            ))}
          </Box>
        )}
        {/* The backend runs the same check on what it saves: a pair it still finds, in its words. */}
        {serverNote && (
          <Box sx={{ mt: '12px' }}>
            <Note>{serverNote}</Note>
          </Box>
        )}

        {modal && (
          <AudienceGroupModal
            kind={modal.kind}
            initial={modal.audience}
            editing={modal.editing}
            roles={roles}
            usable={usable}
            teamName={teamName}
            lists={otherLists}
            describeIt={describeIt}
            onCancel={() => setModal(null)}
            onConfirm={(a) => confirm(modal.kind, a)}
          />
        )}
      </Box>
    </FormRow>
  );
};
